import { faCheck, faUpload } from "@fortawesome/free-solid-svg-icons";
import { getAvatars } from "./avatar.config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Button, Header4 } from "../../../../components/ui";
import localization from "../../../../config/localization";

interface AvatarProps {
  avatar: string;
  className?: string;
  onChange: (value: string) => void;
  onSubmit: (file: File | null) => void;
}

export function Avatar({ avatar, className, onChange, onSubmit }: AvatarProps) {
  const [selected, setSelected] = useState<string | null>(avatar);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadAvatarError, setUploadAvatarError] = useState<string | null>(null);
  const [oldAvatar, setOldAvatar] = useState<string | null>(avatar);

  const avatars = getAvatars();
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedFileRef = useRef<File | null>(null);

  useEffect(() => {
    if (avatar) {
      setSelected(avatar);
    }
  }, [avatar]);

  const validateImage = (file: File) => {
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      return localization.img_extention;
    }
    if (file.size / 1_000_000 > 5) {
      return localization.img_size;
    }
    return null;
  };

  const handleSelect = (id: string) => {
    setSelected(id);
    setPreview(null);
    setUploadAvatarError(null);
    selectedFileRef.current = null;
    onChange?.(id);
  };

  const handleUpload = (file: File | null) => {
    if (!file) return;

    const error = validateImage(file);

    if (error) {
      setUploadAvatarError(error);
      return;
    }

    setUploadAvatarError(null);

    selectedFileRef.current = file;

    const url = URL.createObjectURL(file);
    setSelected(url);
    setPreview(url);
    onChange?.(url);

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setUploadAvatarError(null);
    selectedFileRef.current = null;
    onChange?.(oldAvatar ?? "");
  };

  return (
    <div>
      <div className={`w-full ${className}`}>

        <div className="mb-6">
          <Header4 className="text-white mb-3 font-semibold">
            {localization.upload_avatar}
          </Header4>

          <div className="flex flex-col items-center">
            <Button className="relative group focus:outline-none flex items-center justify-center">
              <div className="relative inline-block">
                <div
                  className={`h-20 w-20 rounded-full overflow-hidden transition-all border border-white/20 flex items-center justify-center ${selected === preview
                    ? "ring-2 ring-white"
                    : "ring-2 ring-transparent group-hover:ring-gray-500"
                    }`}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    hidden
                    accept="image/png,image/jpeg"
                    onChange={(e) => handleUpload(e.target.files?.[0] ?? null)}
                  />

                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faUpload}
                      className="size-12 cursor-pointer"
                      onClick={() => inputRef.current?.click()}
                    />
                  )}
                </div>

                {selected === preview && preview && (
                  <div className="absolute bottom-0 right-0 translate-x-1 translate-y-1 h-6 w-6 rounded-full bg-white flex items-center justify-center shadow-md">
                    <FontAwesomeIcon
                      icon={faCheck}
                      size="sm"
                      className="text-[#FD4A4A]"
                    />
                  </div>
                )}
              </div>
            </Button>

            {preview && (
              <div className="mt-3">
                <Button
                  className="px-3 py-1 text-sm rounded-md bg-white/10 text-white hover:bg-white/20"
                  onClick={handleRemove}
                >
                  {localization.remove_avatar}
                </Button>
              </div>
            )}

            {uploadAvatarError && (
              <p className="text-red-500 text-xs mt-2 text-center">
                {uploadAvatarError}
              </p>
            )}
          </div>
        </div>

        <div>
          <Header4 className="text-white mb-3 font-semibold">
            {localization.choose_avatar}
          </Header4>

          <div className="grid grid-cols-4 gap-5">
            {avatars.map((avatar) => {
              const isActive = selected === avatar.id;

              return (
                <Button
                  key={avatar.id}
                  onClick={() => handleSelect(avatar.id)}
                  className="relative group focus:outline-none flex items-center justify-center"
                >
                  <div className="relative inline-block">
                    <div
                      className={`h-16 w-16 sm:h-20 sm:w-20 rounded-full overflow-hidden transition-all ${isActive
                        ? "ring-2 ring-white"
                        : "ring-2 ring-transparent group-hover:ring-gray-500"
                        }`}
                    >
                      <img
                        src={avatar.src}
                        alt={avatar.label ?? "avatar"}
                        className="h-full w-full object-cover"
                      />
                    </div>

                    {isActive && (
                      <div className="absolute bottom-0 right-0 translate-x-1 translate-y-1 h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-white flex items-center justify-center shadow-md">
                        <FontAwesomeIcon
                          icon={faCheck}
                          size="sm"
                          className="text-[#FD4A4A]"
                        />
                      </div>
                    )}
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-center">
        <Button
          type="submit"
          className="mt-2 md:w-1/2 sm:mt-4 w-full sm:w-auto py-4 rounded-md text-white bg-primary-gradient flex justify-center"
          onClick={() => onSubmit(selectedFileRef.current)}
        >
          <span className="uppercase tracking-wider">
            {localization.submit}
          </span>
        </Button>
      </div>
    </div>
  );
}